// DocsSection.jsx
// Standalone /docs route: end-user + admin documentation, rendered from Markdown.
// Reachable at tk.tripleco.com/docs, and linked from every project's nav via an
// absolute https://tk.tripleco.com/docs link (works from any project domain,
// including datamvps.tripleco.com, which never renders ProjectSelector).

import { useState, useEffect } from "react";
import ReactMarkdown from "react-markdown";
import { useMsal, useIsAuthenticated } from "@azure/msal-react";
import { loginRequest } from "./authConfig";
import { DOCS_NAV } from "./docs-content/manifest";
import Footer from "./Footer";

const C = {
  dark: "#1a1a2e", gold: "#c9a84c", white: "#ffffff",
  border: "#e5e7eb", muted: "#6b7280", text: "#374151", light: "#f8f7f4",
};

export default function DocsSection() {
  const { instance } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [sectionId, setSectionId] = useState(DOCS_NAV[0].id);
  const [pageId, setPageId] = useState(DOCS_NAV[0].pages[0].id);
  const [content, setContent] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const section = DOCS_NAV.find(function (s) { return s.id === sectionId; });
  const page = section.pages.find(function (p) { return p.id === pageId; });

  useEffect(function () {
    if (!isAuthenticated) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetch(page.src)
      .then(function (res) {
        if (!res.ok) throw new Error("Request failed: " + res.status);
        return res.text();
      })
      .then(function (text) {
        if (cancelled) return;
        setContent(text);
        setLoading(false);
      })
      .catch(function () {
        if (cancelled) return;
        setError("Could not load this page.");
        setLoading(false);
      });
    return function () { cancelled = true; };
  }, [isAuthenticated, page.src]);

  function handleLogin() {
    instance.loginPopup(loginRequest).catch(function (e) {
      if (e.errorCode !== "user_cancelled") console.error(e);
    });
  }

  function selectPage(sId, pId) {
    setSectionId(sId);
    setPageId(pId);
  }

  if (!isAuthenticated) {
    return (
      <div style={{ minHeight: "100vh", background: C.light, fontFamily: "system-ui, -apple-system, sans-serif", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ textAlign: "center", padding: 40 }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: C.dark, marginBottom: 8 }}>TrapperKeeper Docs</div>
          <div style={{ fontSize: 13, color: C.muted, marginBottom: 20 }}>Sign in with your TCC Microsoft account to view documentation.</div>
          <button onClick={handleLogin} style={{ padding: "8px 20px", background: C.gold, border: "none", color: C.dark, borderRadius: 6, fontSize: 13, cursor: "pointer", fontWeight: 700 }}>
            Sign In with TCC
          </button>
          <div style={{ marginTop: 32, textAlign: "left" }}>
            <Footer />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: C.light, fontFamily: "system-ui, -apple-system, sans-serif", fontSize: 13 }}>
      <div style={{ background: C.dark, padding: "14px 32px", display: "flex", alignItems: "center", gap: 12 }}>
        <a href="https://tk.tripleco.com" style={{ fontSize: 12, color: C.gold, textDecoration: "none", fontWeight: 700 }}>← TrapperKeeper</a>
        <span style={{ color: "#4b5563" }}>›</span>
        <div style={{ fontSize: 13, color: "#9ca3af" }}>Docs</div>
      </div>

      <div style={{ display: "flex", maxWidth: 1080, margin: "0 auto" }}>
        <div style={{ width: 200, flexShrink: 0, padding: "24px 16px", borderRight: "1px solid " + C.border }}>
          {DOCS_NAV.map(function (s) {
            return (
              <div key={s.id} style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: C.dark, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>{s.label}</div>
                {s.pages.map(function (p) {
                  const active = s.id === sectionId && p.id === pageId;
                  return (
                    <div key={p.id} onClick={function () { selectPage(s.id, p.id); }}
                      style={{ padding: "6px 8px", borderRadius: 6, cursor: "pointer", fontSize: 12, color: active ? C.dark : C.muted, fontWeight: active ? 700 : 400, background: active ? C.white : "transparent", border: active ? "1px solid " + C.border : "1px solid transparent" }}>
                      {p.label}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>

        <div style={{ flex: 1, padding: "24px 32px", minWidth: 0 }}>
          {loading && <div style={{ color: C.muted }}>Loading...</div>}
          {error && <div style={{ color: "#c0392b" }}>{error}</div>}
          {!loading && !error && (
            <div style={{ background: C.white, border: "1px solid " + C.border, borderRadius: 8, padding: "20px 28px", color: C.text, lineHeight: 1.6 }}>
              <ReactMarkdown>{content}</ReactMarkdown>
            </div>
          )}
        </div>
      </div>

      <div style={{ maxWidth: 1080, margin: "0 auto", padding: "0 32px" }}>
        <Footer />
      </div>
    </div>
  );
}
